import { useEffect, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  TextFieldProps,
  Typography,
  outlinedInputClasses,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import { BillsTable } from './BillsTable';
import DateRangePicker, { DateRangeValues } from '../../components/DateRangePicker';
import useQuery from '../../hooks/useQuery';
import { currencySelectMenuProps, supportedCurrencies } from '../../lib/currencies';
import { getErrorMessage } from '../../lib/error';
import { BillStatus, FVBill } from '../../lib/interface';
import { ListBills } from '../../lib/useDeveloperApi';
import { getNumericVal } from '../../utils/number';

const billStatuses: BillStatus[] = ['UNPAID', 'PAID', 'CANCELLED', 'FAILED'];
const DEFAULT_STATUSES: BillStatus[] = [];

/** Absent param → no status filter; empty string → no status filter (user cleared all). */
const statusesFromQuery = (statusesParam: string | null): BillStatus[] => {
  if (statusesParam === null) return DEFAULT_STATUSES;
  if (statusesParam === '') return [];
  return statusesParam.split(',') as BillStatus[];
};

export const ViewBills = ({ customerAppId }: { customerAppId: string }) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [bills, setBills] = useState<FVBill[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 100;
  const query = useQuery();

  const offset = getNumericVal(query.get('offset'), 0);
  const [dateRangeValue, setDateRangeValue] = useState<DateRangeValues>([null, null]);
  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<BillStatus[]>(DEFAULT_STATUSES);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);

  const { data, error, isLoading } = ListBills(customerAppId, {
    offset: getNumericVal(query.get('offset'), 0),
    limit: pageSize,
    date_from: query.get('date_from') ?? undefined,
    date_to: query.get('date_to') ?? undefined,
    statuses: statusesFromQuery(query.get('statuses')),
    search_text: query.get('search_text') ?? undefined,
    currencies: query.get('currencies') ? query.get('currencies')!.split(',') : undefined,
  });

  useEffect(() => {
    const dateFrom = query.get('date_from');
    const dateTo = query.get('date_to');
    if (dateFrom !== null || dateTo !== null) {
      setDateRangeValue((prev) => [
        dateFrom !== null ? new Date(dateFrom) : prev[0],
        dateTo !== null ? new Date(dateTo) : prev[1],
      ]);
    }
    const st = query.get('search_text');
    if (st !== null) setSearchText(st);

    setSelectedStatuses(statusesFromQuery(query.get('statuses')));

    const currencies = query.get('currencies');
    if (currencies !== null) setSelectedCurrencies(currencies.split(','));
  }, [query]);

  useEffect(() => {
    setBills(data?.bills ?? []);
    setHasMore(data?.has_more ?? false);
    if (data) {
      enqueueSnackbar('Loaded bills', { variant: 'info', autoHideDuration: 1000 });
    }
  }, [data, enqueueSnackbar]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const getDateWithOffset = (date: Date) => {
    const d = new Date(date.getTime());
    d.setHours(0);
    d.setMinutes(-date.getTimezoneOffset());
    return d;
  };

  const submitForm = () => {
    const params = new URLSearchParams();
    if (dateRangeValue[0] !== null) params.set('date_from', dateRangeValue[0].toISOString().split('T')[0]);
    if (dateRangeValue[1] !== null) params.set('date_to', dateRangeValue[1].toISOString().split('T')[0]);
    if (searchText !== '') params.set('search_text', searchText.trimEnd());
    params.set('statuses', selectedStatuses.length > 0 ? selectedStatuses.join(',') : '');
    if (selectedCurrencies.length > 0) params.set('currencies', selectedCurrencies.join(','));
    history.push({ search: params.toString() });
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitForm();
        }}
      >
        <Grid container spacing={2}>
          <Grid container item spacing={1} display="flex" alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="search-bar"
                variant="outlined"
                label="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Search by Bill Reference, User ID, or External User ID."
              />
            </Grid>
          </Grid>
          <Grid container item spacing={1} display="flex" justifyContent="start">
            <Grid item xl={2.5} lg={4} md={5} sm={6} xs={12}>
              <DateRangePicker
                values={dateRangeValue}
                views={['day']}
                inputFormat="yyyy-MM-dd"
                onStartDateChange={(newValue) => {
                  setDateRangeValue([newValue === null ? null : getDateWithOffset(newValue), dateRangeValue[1]]);
                }}
                onEndDateChange={(newValue) => {
                  setDateRangeValue([dateRangeValue[0], newValue === null ? null : getDateWithOffset(newValue)]);
                }}
                renderInput={(params: TextFieldProps) => (
                  <Box sx={{ width: 200 }}>
                    <TextField
                      fullWidth
                      {...params}
                      sx={{ [`& .${outlinedInputClasses.root}:hover > fieldset`]: { borderColor: 'primary.dark' } }}
                    />
                  </Box>
                )}
              />
            </Grid>
            <Grid item xl={2} lg={2} md={2} sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="bill-status-label">Status</InputLabel>
                <Select
                  labelId="bill-status-label"
                  id="bill-status-select"
                  value={selectedStatuses}
                  multiple
                  renderValue={(selected) => selected.join(', ')}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedStatuses(typeof v === 'string' ? (v.split(',') as BillStatus[]) : v);
                  }}
                  label="Status"
                >
                  {billStatuses.map((s) => (
                    <MenuItem sx={{ fontSize: '0.8em' }} key={s} value={s}>
                      <Checkbox checked={selectedStatuses.includes(s)} />
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xl={1.5} lg={2} md={2} sm={6} xs={12}>
              <FormControl fullWidth>
                <InputLabel id="currency-select-label">Currency</InputLabel>
                <Select
                  labelId="currency-select-label"
                  id="currency-select"
                  multiple
                  value={selectedCurrencies}
                  label="Currency"
                  onChange={(e) => {
                    const {
                      target: { value },
                    } = e;
                    setSelectedCurrencies(typeof value === 'string' ? value.split(',') : value);
                  }}
                  renderValue={(selected) => selected.join(', ')}
                  MenuProps={currencySelectMenuProps}
                >
                  {supportedCurrencies.map((ccy) => (
                    <MenuItem sx={{ fontSize: '0.8em' }} key={ccy} value={ccy}>
                      <Checkbox checked={selectedCurrencies.includes(ccy)} />
                      {ccy}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={2} xs={12}>
              <Button fullWidth variant="contained" sx={{ height: '56px' }} type="submit">
                View
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>

      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: '1em', marginTop: '0.5em' }}
      >
        <Grid item>
          <Typography variant="h4" component="h4">
            Bills
          </Typography>
        </Grid>
        {!isLoading && bills.length > 0 && (
          <Grid item>
            <Typography variant="subtitle1">
              {offset + 1} - {offset + bills.length} of {hasMore ? 'many' : offset + bills.length}
            </Typography>
          </Grid>
        )}
      </Grid>
      <BillsTable items={bills} isLoading={isLoading} customerAppId={customerAppId} />
      <ButtonGroup
        disableElevation
        size="small"
        aria-label="Pagination"
        variant="contained"
        sx={{ width: '250px', marginTop: '1em' }}
      >
        <Button
          fullWidth
          disabled={offset <= 0}
          onClick={() => {
            query.set('offset', Math.max(offset - pageSize, 0).toString());
            history.push({ search: query.toString() });
          }}
        >
          Previous
        </Button>
        <Button
          fullWidth
          disabled={!hasMore}
          onClick={() => {
            query.set('offset', (offset + pageSize).toString());
            history.push({ search: query.toString() });
          }}
        >
          Next
        </Button>
      </ButtonGroup>
    </>
  );
};
